import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import icView from "@/assets/images/console/icView.svg";
import LoadingSpinner from "@/components/common/common/LoadingSpinner";
import Editor from "@/components/console/form/Editor";
import { FileData } from "@/components/console/form/FileUpload";
import { API_URL } from "@/config/apiConfig";
import { useDelBoard, useGetBoard, useGetBoardFileDownload } from "@/service/console/board";
import { usePopupStore } from "@/store/common/usePopupStore";
import { makeIntComma } from "@/utils/numberUtils";

interface InfoItem {
    b_title: string;
    m_name: string;
    b_reg_date: string;
    b_view: number;
    b_contents: string;
    b_file: FileData[];
    b_img: string | null;
}

export default function BoardDetail() {
    const navigate = useNavigate();
    const { category, board_idx } = useParams();
    const [downloadFile, setDownloadFile] = useState<{ idx: string; name: string } | null>(null);
    const { data: configData, isLoading: isInitialLoading } = useGetBoard(category || "", board_idx || "", {
        enabled: Boolean(board_idx),
    });
    const { data: downloadData, isLoading: isFileDownloadLoading } = useGetBoardFileDownload(
        category || "",
        board_idx || "",
        downloadFile?.idx || "",
    );
    const delBoardMutation = useDelBoard();
    const initialInfo = useMemo<InfoItem>(
        () => ({
            b_title: "",
            m_name: "",
            b_reg_date: "",
            b_view: 0,
            b_contents: "",
            b_file: [],
            b_img: null,
        }),
        [],
    );
    const [info, setInfo] = useState<InfoItem>(initialInfo);
    const { setConfirmPop, setLoadingPop } = usePopupStore();

    // 데이터 로딩 중일 때 로딩 팝업 표시
    useEffect(() => {
        const isLoading = isFileDownloadLoading || delBoardMutation.isPending;
        setLoadingPop(isLoading, true);
        return () => setLoadingPop(false);
    }, [isFileDownloadLoading, delBoardMutation.isPending, setLoadingPop]);

    // 게시글 상세 조회
    useEffect(() => {
        if (configData) {
            setInfo(configData.data);
        } else {
            setInfo(initialInfo);
        }
    }, [configData, initialInfo]);

    // 파일다운로드 데이터가 있을 때 실행
    useEffect(() => {
        if (downloadData && downloadFile) {
            const url = window.URL.createObjectURL(new Blob([downloadData.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", downloadFile.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setDownloadFile(null); // 다운로드 완료 후 초기화
        }
    }, [downloadData, downloadFile]);

    // 첨부파일 다운로드 버튼 클릭시
    const handleFileDownload = (idx: string, file_name: string) => {
        if (!category || !board_idx) return;
        setDownloadFile({ idx, name: file_name });
    };

    // 삭제 확인
    const handleConfirmDelete = () => {
        setConfirmPop(true, "삭제 시 복구가 불가합니다. 삭제하시겠습니까?", 2, () => handleDelete());
    };

    // 삭제하기
    const handleDelete = () => {
        if (!category || !board_idx) return;
        const body = { idx: [board_idx], category: Number(category) };
        delBoardMutation.mutate(body, {
            onSuccess: () => {
                navigate(-1);
            },
        });
    };

    return (
        <div className="rounded-[8px] bg-white p-[36px_40px] shadow-[0_0_8px_0_rgba(0,0,0,0.05)]">
            <div className="py-[10px] text-[24px] font-[600]">
                {category === "166"
                    ? "공지사항"
                    : category === "167"
                      ? "FAQ"
                      : category === "170"
                        ? "인스타그램"
                        : "관련자료"}
            </div>
            <div className="mt-[20px] border-b border-t border-[#222]">
                {isInitialLoading ? (
                    <div className="flex h-[400px] items-center justify-center">
                        <LoadingSpinner console />
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between border-b border-[#ddd] p-[30px_20px]">
                            <div className="flex flex-col gap-[20px]">
                                <p className="text-[28px] font-[500]">{info.b_title}</p>
                                <ul className="flex items-center gap-[16px] text-[20px] text-[#999]">
                                    <li>{info.m_name}</li>
                                    <li className="h-[16px] w-[1px] bg-[#ddd]" />
                                    <li>{info.b_reg_date}</li>
                                    <li className="h-[16px] w-[1px] bg-[#ddd]" />
                                    <li className="flex items-center gap-[6px]">
                                        <img src={icView} alt="조회수" />
                                        <p>{makeIntComma(info.b_view)}</p>
                                    </li>
                                </ul>
                            </div>
                            <div className="flex gap-[4px]">
                                <Link
                                    to={`/console/board/${category}/${board_idx}/edit`}
                                    className="rounded-[5px] border border-[#ddd] px-[10px] text-[14px] font-[500] leading-[30px] text-[#666]"
                                >
                                    수정
                                </Link>
                                <button
                                    type="button"
                                    className="height-[30px] rounded-[5px] border border-[#ddd] bg-[#f3f3f3] px-[10px] text-[14px] font-[500] text-[#666]"
                                    onClick={handleConfirmDelete}
                                >
                                    삭제
                                </button>
                            </div>
                        </div>
                        <div className="min-h-[200px] border-b border-[#ddd] p-[30px_5px]">
                            {info.b_img && (
                                <div className="flex justify-center pb-[20px]">
                                    <img src={`${API_URL}/${info.b_img}`} alt="미리보기 이미지" />
                                </div>
                            )}
                            <Editor value={info.b_contents} readOnly />
                        </div>
                        {info.b_file.length > 0 && (
                            <div className="flex py-[25px]">
                                <p className="w-[120px] text-[20px] font-[500]">첨부파일</p>
                                <ul className="flex flex-1 flex-col gap-[5px]">
                                    {info.b_file.map((file, i) => (
                                        <li key={`file_${i}`}>
                                            <button
                                                type="button"
                                                className="text-left text-[18px] text-[#999]"
                                                onClick={() =>
                                                    handleFileDownload(file.idx.toString(), file.original_name)
                                                }
                                            >
                                                {file.original_name}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="mt-[30px] flex justify-center">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="h-[50px] w-[240px] rounded-[5px] border border-[#ddd] text-center text-[18px] font-[500] text-[#666]"
                >
                    목록
                </button>
            </div>
        </div>
    );
}
